import type { LucideIcon } from "lucide-react-native";
import { useMemo } from "react";

import type { MobileActionMenuViewItem } from "./MobileActionMenuView";
import MobileActionMenuView from "./MobileActionMenuView";

/** A single action offered by the menu. */
export interface MobileActionMenuItem {
  /** Optional leading lucide icon. */
  readonly icon?: LucideIcon;
  /** Row label. */
  readonly label: string;
  /** Optional secondary line below the label. */
  readonly description?: string;
  /** Action to run after the menu closes. */
  readonly onPress: () => void;
  /** Style the row as a destructive action. */
  readonly destructive?: boolean;
  /** Render the row inert and dimmed. */
  readonly disabled?: boolean;
  /** Row testID. */
  readonly testID?: string;
}

/** Props for MobileActionMenu */
export interface MobileActionMenuProps {
  /** Whether the menu sheet is open. */
  readonly isOpen: boolean;
  /** Unified dismissal callback. */
  readonly onClose: () => void;
  /** Optional sheet title. */
  readonly title?: string;
  /** Optional secondary line pinned under the title. */
  readonly subtitle?: string;
  /** Menu actions, rendered in order. */
  readonly items: readonly MobileActionMenuItem[];
  /** Root testID; rows derive theirs when not explicitly set. */
  readonly testID?: string;
}

/**
 * Canonical mobile action menu (Container). Wraps each item's press so the
 * menu closes BEFORE the action runs — the sequencing every migrated menu
 * used, and required when the action opens another overlay (the chrome must
 * unmount before a sibling sheet/modal presents on native).
 * @param props - Component props
 * @param props.isOpen - Whether the menu sheet is open
 * @param props.onClose - Unified dismissal callback
 * @param props.title - Optional sheet title
 * @param props.subtitle - Optional secondary line pinned under the title
 * @param props.items - Menu actions, rendered in order
 * @param props.testID - Root testID; rows derive theirs when not set
 */
const MobileActionMenuContainer = ({
  isOpen,
  onClose,
  title,
  subtitle,
  items,
  testID,
}: MobileActionMenuProps) => {
  const wiredItems = useMemo<readonly MobileActionMenuViewItem[]>(
    () =>
      items.map(item => ({
        ...item,
        onPress: () => {
          onClose();
          item.onPress();
        },
      })),
    [items, onClose]
  );

  return (
    <MobileActionMenuView
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      items={wiredItems}
      testID={testID}
    />
  );
};

MobileActionMenuContainer.displayName = "MobileActionMenuContainer";

export default MobileActionMenuContainer;
